// The nine elements, drawn with the console's own components from the
// declaration (02-DESIGN/14-solutions/03-the-console.md § the elements):
// every subject built through the library's builder — the role resolved to
// its workload id from the install record — and placed on the person's own
// connection, admitted or refused by the broker as any request of theirs. A
// refusal is shown as the console shows every refusal; a call nothing
// answers, or that gets no answer in time, ends in words, never a wait. A
// list is the one table — its Filter field, a menu per pill column and per
// declared filter on a field no column draws, its header sort, newest first
// where it has a time — showing its top ten rows with *Show more*; a row's
// detail is the record's page at `<entry>/<panel>/<key>` and never expanded in
// the table, its other actions behind the row menu; a declared form is a
// form in the panel's place (14-solutions/03 § the elements; hq decision 0069); who asked and what was called read in words,
// an id quiet beside the name, shortened from the middle with copy
// (02-DESIGN/03-console/01-the-console.md § conventions; hq issue 183).

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import {
  Button,
  Confirm,
  Dot,
  Empty,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  Select,
  TextInput,
  Tile,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { Table, type Column, type Facet } from "../../ui/table.tsx";
import { Subjects as AuthSubjects, type AppsListReply } from "../../wire/auth.ts";
import { SERVICE as IDENTITY } from "../../wire/identity.ts";
import {
  listSubject as workloadsListSubject,
  SERVICE as WORKLOADS,
  type ListReply as WorkloadsListReply,
} from "../../wire/workloads.ts";
import {
  DOCUMENT_SETTINGS,
  ElementKind,
  FieldKind,
  secretPath,
  serviceOf,
  verbSubject,
  type Element,
  type Field,
  type Manifest,
} from "../../wire/solutions.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Ident, NameLink, shorten } from "../../ui/ident.tsx";
import { TOP } from "../../ui/more.tsx";
import { useSurface } from "../surface.tsx";
import { escalationOf } from "../approvals/card.ts";
import { Escalated } from "../approvals/Escalated.tsx";
import { ChatEl } from "./chat/Chat.tsx";
import { FieldInput, parseValue, valueText } from "./fields.tsx";
import { useInstalled } from "./installed.ts";
import { pageOffered } from "../../roles/offers.ts";
import {
  addressWords,
  fieldPath,
  rankedPath,
  rowName,
  whoOf,
  whoPath,
  type Known,
  type Linked,
  type Names,
  type Who,
} from "./who.ts";
import "./elements.css";
import {
  bodyOf,
  cellItems,
  dayOf,
  detailKey,
  formKey,
  grouped,
  heldColumns,
  heldFields,
  peakLine,
  keyOf,
  KNOWN_ELEMENTS,
  opensOn,
  opensOnFirstCell,
  pointsOf,
  recordList,
  stateOf,
  validRecordKey,
  troubleOf,
  unplaced,
  withFrame,
  type Point,
  type Row,
  type Selection,
} from "./panel.ts";

/** Ctx is what every element of a solution's panel draws from. */
export interface Ctx {
  m: Manifest;
  /** The workload id per role, from the install record. */
  ids: Readonly<Record<string, string>>;
  admin: boolean;
  /** The panel's selection, which every reading element sends. */
  selection?: Selection;
  /** select chooses a value for one of the panel's selection fields, or removes it. */
  select?: (field: string, value?: string, label?: string) => void;
  /** recordTo is the address of a record's page under the panel, `<entry>/<panel>/<key>`; without it no row opens a record. */
  recordTo?: (key: string) => string;
}

const NONE: Selection = {};

/** target resolves an element's verb to the workload that serves it: its service, its id and the verb. */
function target(ctx: Ctx, e: Element): { service: string; id: string; verb: string } | undefined {
  const id = e.role !== undefined ? ctx.ids[e.role] : undefined;
  if (id === undefined || e.verb === undefined) {
    return undefined;
  }
  return { service: serviceOf(ctx.m, e.role ?? "", id), id, verb: e.verb };
}

/**
 * Refused shows what a call ended in: *Not running here* or *No answer* where
 * nothing answered it; a `guard-escalated` as the Approvals card where the
 * call stopped, with *Approve*, since the element does not ask again itself
 * (14-solutions/03 § the words; hq decision 0083 § 6); otherwise as every
 * refusal is shown.
 */
function Refused({ err, what }: { err: unknown; what?: string }) {
  const escalation = escalationOf(err);
  if (escalation !== undefined) {
    return <Escalated key={escalation} id={escalation} />;
  }
  const t = troubleOf(err);
  if (t === undefined) {
    return <NotAllowed err={err} what={what} />;
  }
  return <Notice title={what === undefined ? t.title : `${what}: ${t.title.toLowerCase()}`}>{t.text}</Notice>;
}

export function Elements({ ctx, elements }: { ctx: Ctx; elements: Element[] }) {
  // The one list whose rows open the record's page: the address names the panel and the key.
  const records = recordList(elements);
  return (
    <>
      {grouped(elements).map((g, i) => (
        <section className="section" key={`${g.element.kind}-${String(i)}`}>
          <One
            ctx={g.element === records ? ctx : { ...ctx, recordTo: undefined }}
            e={g.element}
            rowActions={g.rowActions}
          />
        </section>
      ))}
    </>
  );
}

function One({ ctx, e, rowActions }: { ctx: Ctx; e: Element; rowActions: Element[] }) {
  if (!KNOWN_ELEMENTS.has(e.kind)) {
    return <Notice>This panel needs a newer console.</Notice>;
  }
  switch (e.kind) {
    case ElementKind.list:
      return <List ctx={ctx} e={e} rowActions={rowActions} />;
    case ElementKind.record:
      return <RecordEl ctx={ctx} e={e} />;
    case ElementKind.form:
      return <FormOpener e={e} />;
    case ElementKind.action:
      return <Action ctx={ctx} e={e} />;
    case ElementKind.chart:
      return <Chart ctx={ctx} e={e} />;
    case ElementKind.status:
      return <Status ctx={ctx} e={e} />;
    case ElementKind.setting:
      return <SettingEl ctx={ctx} e={e} />;
    case ElementKind.chat:
      return <ChatEl ctx={ctx} e={e} />;
  }
  return <Notice>This panel needs a newer console.</Notice>;
}

/** useStream reads a verb's answer — once, or live where the element is a watch — into state. */
function useStream<T>(
  ctx: Ctx,
  e: Element,
  body: unknown,
  fold: (prev: T, frame: unknown) => T,
  initial: T,
  key?: string,
): { value: T; err: unknown; done: boolean } {
  const s = useSurface();
  const [state, setState] = useState<{ value: T; err: unknown; done: boolean }>({
    value: initial,
    err: undefined,
    done: false,
  });
  const bodyKey = JSON.stringify(body ?? null);
  useEffect(() => {
    const t = target(ctx, e);
    if (t === undefined) {
      return;
    }
    const ac = new AbortController();
    let value = initial;
    s.bus
      .stream(
        t.service,
        verbSubject(t.id, t.verb, key),
        body,
        (f) => {
          value = fold(value, f);
          setState({ value, err: undefined, done: false });
        },
        { signal: ac.signal, follow: e.watch === true },
      )
      .then(
        () => {
          if (!ac.signal.aborted) {
            setState({ value, err: undefined, done: true });
          }
        },
        (err: unknown) => {
          if (!ac.signal.aborted) {
            setState({ value, err, done: true });
          }
        },
      );
    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the element, its body and its key say when to read again
  }, [e.role, e.verb, bodyKey, key]);
  if (target(ctx, e) === undefined) {
    return { value: initial, err: unplaced(e.role), done: true };
  }
  return state;
}

/** Cell is one value as its kind draws it; `to` is the page the value names, where it has one. */
function Cell({ c, v, to }: { c: Field; v: unknown; to?: string }) {
  if (Array.isArray(v)) {
    return (
      <span className="items">
        {cellItems(v).map((x, i) => (
          <Cell key={String(i)} c={c} v={x} />
        ))}
      </span>
    );
  }
  switch (c.kind) {
    case FieldKind.time:
      return <span>{when(typeof v === "string" ? v : undefined)}</span>;
    case FieldKind.pill:
      return v === undefined ? <span className="faint">—</span> : <Pill>{valueText(v)}</Pill>;
    case FieldKind.id:
      return v === undefined || v === null || valueText(v) === "" ? (
        <span className="faint">—</span>
      ) : (
        <Ident value={valueText(v)} to={to} />
      );
    default:
      return to !== undefined ? <NameLink to={to}>{valueText(v)}</NameLink> : <span>{valueText(v)}</span>;
  }
}

function List({ ctx, e, rowActions }: { ctx: Ctx; e: Element; rowActions: Element[] }) {
  const names = useNames(ctx);
  const [acted, setActed] = useState<{ label: string; err?: unknown } | undefined>(undefined);
  const s = useSurface();
  const {
    value: rows,
    err,
    done,
  } = useStream<Row[]>(ctx, e, bodyOf(e, ctx.selection ?? NONE), (prev, f) => withFrame(prev, f, e.key), []);
  // A declared column no row holds is left out (14-solutions/03 § the
  // elements); a row's name is still its first declared column.
  const declared = e.columns ?? [];
  const cols = heldColumns(declared, rows);
  const chosen = e.selects === undefined ? undefined : ctx.selection?.[e.selects]?.value;
  const byPerson = isByPerson(e);
  const items: Item[] = rows.map((r, i) => ({ r, i, k: keyOf(r, e.key), dk: detailKey(e, r) }));
  const named = (it: Item): Who => {
    const first = declared[0];
    return rowName(first === undefined ? "" : valueText(it.r[first.name] ?? ""), it.k, names);
  };
  const choose = (it: Item) => {
    if (e.selects === undefined || it.k === undefined || ctx.select === undefined) {
      return;
    }
    const w = named(it);
    ctx.select(e.selects, it.k, w.name !== "" ? w.name : it.k);
  };
  const selects = e.selects !== undefined && ctx.select !== undefined;
  // A row does one thing: opens its record's page, or selects (14-solutions/03 § the elements).
  const recordTo = e.record !== undefined ? ctx.recordTo : undefined;
  const act = (a: Element, key: string | undefined) => {
    const t = target(ctx, a);
    const label = a.label ?? a.verb ?? "";
    if (t === undefined) {
      setActed({ label, err: unplaced(a.role) });
      return;
    }
    setActed(undefined);
    s.bus.call(t.service, verbSubject(t.id, t.verb, key), undefined, 30000).then(
      () => {
        setActed({ label });
      },
      (x: unknown) => {
        setActed({ label, err: x });
      },
    );
  };
  const may = (page: Linked) => pageOffered(s.role, page);
  // The first cell is the row's own opener where the row opens — its record's
  // page, or its selection — and holds no second link; every other name links.
  const links: Links = {
    opens: (it) => opensOnFirstCell(recordTo !== undefined, selects, it.dk),
    who: (w) => whoPath(w, s.tenant, may),
    field: (name, r) => fieldPath(name, r, s.tenant, may),
    ranked: (w) => rankedPath(w, e.params?.dimension, s.tenant, may),
  };
  const columns = cols.map((c, i) => columnOf(c, i, e, names, byPerson, links));
  const timed = cols.find((c) => c.kind === FieldKind.time);
  const shownNames = new Set(cols.map((c) => c.name));
  // A declared filter on a field no column draws is a menu over the rows'
  // values; one on a drawn column is the Filter field's, or the column's menu.
  const facets: Facet<Item>[] = (e.filters ?? [])
    .filter((f) => !shownNames.has(f.name) && rows.some((r) => typeof r[f.name] === "string"))
    .map((f) => ({ key: `filter:${f.name}`, facet: f.label, value: (it) => valueText(it.r[f.name] ?? "") }));
  return (
    <>
      {e.label !== undefined && (
        <div className="section-head">
          <h2>{e.label}</h2>
          {e.watch === true && <span className="small faint">live</span>}
        </div>
      )}
      {acted !== undefined &&
        (acted.err !== undefined ? (
          <Refused err={acted.err} what={acted.label} />
        ) : (
          <Notice kind="ok">{acted.label}: done.</Notice>
        ))}
      {err !== undefined && <Refused err={err} />}
      {err === undefined && rows.length === 0 && !done && <Pending what="Reading" />}
      {err === undefined && rows.length === 0 && done && <Empty>Nothing here yet.</Empty>}
      {rows.length > 0 && (
        <Table<Item>
          label={e.label ?? "Rows"}
          rows={items}
          columns={columns}
          facets={facets}
          rowKey={(it) => it.k ?? String(it.i)}
          // Newest first for records; a ranked list keeps the order the verb answered.
          sort={timed !== undefined ? { key: timed.name, dir: "desc" } : { key: "", dir: "asc" }}
          top={TOP}
          to={
            recordTo === undefined ? undefined : (it) => (it.dk === undefined ? undefined : recordTo(it.dk))
          }
          onOpen={recordTo === undefined && selects ? choose : undefined}
          current={chosen}
          menu={
            rowActions.length === 0
              ? undefined
              : (it) =>
                  rowActions
                    .filter((a) => ctx.admin || !isAdmin(ctx, a))
                    .map((a) => {
                      const key = keyOf(it.r, a.row_key);
                      const label = a.label ?? a.verb ?? "";
                      return a.undoable === true
                        ? {
                            label,
                            onSelect: () => {
                              act(a, key);
                            },
                          }
                        : {
                            label,
                            onSelect: () => {
                              act(a, key);
                            },
                            confirm: { title: `${label}?`, consequence: a.consequence ?? "" },
                          };
                    })
          }
        />
      )}
    </>
  );
}

/** Item is one row of a declared list: what the verb answered, its place, its key and its detail's key. */
interface Item {
  r: Row;
  i: number;
  k?: string;
  dk?: string;
}

/** isByPerson says whether a ranked list ranks by person, where an app or a workload among its rows is marked as such. */
function isByPerson(e: Element): boolean {
  const d = e.params?.dimension;
  return d === "principal" || d === "person";
}

const KIND_WORD: Partial<Record<Who["kind"], string>> = {
  app: "app",
  workload: "workload",
  solution: "solution",
};

/**
 * WhoCell is a caller or a row's name: the name, the identifier shortened
 * beside it with copy, and how it is known; where the caller has a page the
 * role is offered, `to`, the name and the identifier link to it.
 */
function WhoCell({ w, mark, copy = true, to }: { w: Who; mark?: boolean; copy?: boolean; to?: string }) {
  const word = mark === true ? KIND_WORD[w.kind] : undefined;
  return (
    <span className="who">
      {w.name !== "" &&
        (to !== undefined ? (
          <span className="who-name">
            <NameLink to={to}>{w.name}</NameLink>
          </span>
        ) : (
          <span className={w.kind === "nobody" ? "who-name faint" : "who-name"}>{w.name}</span>
        ))}
      {word !== undefined && <span className="who-mark">{word}</span>}
      {w.attested === true && (
        <span className="who-mark" title="attested by the platform">
          attested
        </span>
      )}
      {w.id !== undefined &&
        w.id !== w.name &&
        (copy ? (
          <Ident value={w.id} label="id" to={to} />
        ) : (
          // The row's first cell opens the row, so it holds no button of its own: the whole id is on hover.
          <span className="ident mono" title={w.id}>
            {shorten(w.id)}
          </span>
        ))}
    </span>
  );
}

/** clockOf is a time to the minute, the run a record's time is drawn once for. */
function clockOf(v: unknown): string {
  return typeof v === "string" ? v.slice(0, 16) : "";
}

/**
 * Links are where a declared list's names go (01-the-console.md § conventions:
 * a name is a link): who asked, a field the record names, a ranked row's
 * name; and whether a row opens on its first cell, which then holds no link.
 */
interface Links {
  opens: (it: Item) => boolean;
  who: (w: Who) => string | undefined;
  field: (name: string, r: Row) => string | undefined;
  ranked: (w: Who) => string | undefined;
}

/**
 * columnOf is one declared column as a column of the one table: a record's
 * caller and address named in words, its time once per minute with the full
 * stamp on hover, a pill column with its menu, a ranked list's first column
 * named by the app or workload its key is, every column sortable on its value.
 */
function columnOf(
  c: Field,
  i: number,
  e: Element,
  names: Names,
  byPerson: boolean,
  links: Links,
): Column<Item> {
  const base = { key: c.name, header: c.label };
  /** opener says whether this column's cell is the row's own link or button. */
  const opener = (it: Item) => i === 0 && links.opens(it);
  /** fieldTo is the page this column's value names on a row, where the cell is not the row's opener. */
  const fieldTo = (it: Item) => (opener(it) ? undefined : links.field(c.name, it.r));
  if (c.name === "who") {
    return {
      ...base,
      width: "minmax(0, 1.2fr)",
      value: (it) => whoOf(it.r, names).name,
      text: (it) => {
        const w = whoOf(it.r, names);
        return `${w.name} ${w.id ?? ""}`;
      },
      cell: (it) => {
        const w = whoOf(it.r, names);
        return <WhoCell w={w} to={opener(it) ? undefined : links.who(w)} />;
      },
    };
  }
  if (c.name === "address") {
    const say = (it: Item) => addressWords(valueText(it.r.address ?? ""), names);
    return {
      ...base,
      width: "minmax(0, 2fr)",
      value: (it) => say(it) ?? valueText(it.r.address ?? ""),
      text: (it) => `${say(it) ?? ""} ${valueText(it.r.address ?? "")}`,
      cell: (it) => {
        const words = say(it);
        const raw = valueText(it.r.address ?? "");
        return words !== undefined ? (
          <span className="clip" title={raw}>
            {words}
          </span>
        ) : raw === "" ? (
          <span className="faint">—</span>
        ) : (
          <Ident value={raw} label="subject" head={22} tail={14} />
        );
      },
    };
  }
  if (c.kind === FieldKind.time) {
    return {
      ...base,
      width: "minmax(0, 0.9fr)",
      value: (it) => {
        const v = it.r[c.name];
        return typeof v === "string" ? Date.parse(v) || 0 : 0;
      },
      text: (it) => when(typeof it.r[c.name] === "string" ? (it.r[c.name] as string) : undefined),
      run: (it) => clockOf(it.r[c.name]),
      cell: (it) => {
        const v = it.r[c.name];
        return (
          <span className="clip" title={typeof v === "string" ? v : undefined}>
            {when(typeof v === "string" ? v : undefined)}
          </span>
        );
      },
    };
  }
  if (c.kind === FieldKind.pill) {
    return {
      ...base,
      width: "minmax(0, 0.8fr)",
      facet: c.label,
      value: (it) => valueText(it.r[c.name] ?? ""),
      cell: (it) => <Cell c={c} v={it.r[c.name]} />,
    };
  }
  if (c.kind === FieldKind.number) {
    return {
      ...base,
      width: "minmax(0, 0.7fr)",
      align: "end",
      value: (it) => {
        const v = it.r[c.name];
        return typeof v === "number" ? v : Number(v ?? 0) || 0;
      },
      cell: (it) => <Cell c={c} v={it.r[c.name]} />,
    };
  }
  if (i === 0 && e.key !== undefined) {
    const named = (it: Item) => rowName(valueText(it.r[c.name] ?? ""), it.k, names);
    return {
      ...base,
      width: "minmax(0, 2fr)",
      value: (it) => {
        const w = named(it);
        return w.name !== "" ? w.name : (w.id ?? "");
      },
      text: (it) => {
        const w = named(it);
        return `${w.name} ${w.id ?? ""}`;
      },
      cell: (it) => {
        const w = named(it);
        // The row's opener holds no button of its own; a row that does not open links its name.
        return opener(it) ? (
          <WhoCell w={w} mark={byPerson} copy={false} />
        ) : (
          <WhoCell w={w} mark={byPerson} to={links.ranked(w)} />
        );
      },
    };
  }
  return {
    ...base,
    value: (it) => valueText(it.r[c.name] ?? ""),
    cell: (it) => (
      <span className="clip">
        <Cell c={c} v={it.r[c.name]} to={fieldTo(it)} />
      </span>
    ),
  };
}

/**
 * useNames reads what the page can name a row's identifiers by: the apps the
 * person may see, the workspace's workloads where the role may list them,
 * and the installed solutions — the panel's own workloads among them even
 * where the role lists none. A read refused leaves the identifier shown.
 */
function useNames(ctx: Ctx): Names {
  const s = useSurface();
  const installed = useInstalled(s.bus);
  const runs = s.offered("workloads:list:any");
  const [apps] = useLoad("solution/names/apps", () =>
    s.bus.call<AppsListReply>(IDENTITY, AuthSubjects.appsList(), undefined),
  );
  const [workloads] = useLoad(`solution/names/workloads/${runs ? "any" : "none"}`, () =>
    runs
      ? s.bus.call<WorkloadsListReply>(WORKLOADS, workloadsListSubject(), undefined)
      : Promise.resolve<WorkloadsListReply>({ workloads: [] }),
  );
  const solutions = new Map<string, string>(installed.map((r) => [r.name, r.manifest?.display ?? r.name]));
  solutions.set(ctx.m.name, ctx.m.display);
  const known = new Map<string, Known>();
  if (workloads.state === "ready") {
    for (const w of workloads.value.workloads) {
      const sol = w.solution?.name;
      known.set(w.id, {
        name: w.name,
        solution: sol === undefined ? undefined : (solutions.get(sol) ?? sol),
      });
    }
  }
  for (const id of Object.values(ctx.ids)) {
    known.set(id, { name: known.get(id)?.name ?? ctx.m.display, solution: ctx.m.display });
  }
  const appNames = new Map<string, string>(
    apps.state === "ready" ? apps.value.apps.map((a) => [a.id, a.name]) : [],
  );
  return { apps: appNames, workloads: known, solutions };
}

function RecordEl({ ctx, e, recordKey }: { ctx: Ctx; e: Element; recordKey?: string }) {
  const s = useSurface();
  const may = (page: Linked) => pageOffered(s.role, page);
  const { value, err, done } = useStream<Row | undefined>(
    ctx,
    e,
    recordKey === undefined ? bodyOf(e, ctx.selection ?? NONE) : undefined,
    (_prev, f) => (typeof f === "object" && f !== null && !Array.isArray(f) ? (f as Row) : undefined),
    undefined,
    recordKey,
  );
  if (err !== undefined) {
    return <Refused err={err} />;
  }
  if (value === undefined) {
    return done ? <Empty>Nothing to show.</Empty> : <Pending what="Reading" />;
  }
  // A list's row detail is titled by its page; a field the answer does not
  // hold is left out rather than drawn empty.
  const fields = heldFields(e.fields ?? [], value);
  if (e.tiles === true && recordKey === undefined) {
    const figures = fields.filter((f) => f.kind === FieldKind.number);
    const notes = fields.filter((f) => f.kind !== FieldKind.number);
    return (
      <>
        {e.label !== undefined && <h2>{e.label}</h2>}
        {fields.length === 0 && <Empty>Nothing to show.</Empty>}
        <div className="tiles">
          {figures.map((f) => (
            <Tile key={f.name} label={f.label} value={valueText(value[f.name])} />
          ))}
        </div>
        {notes.map((f) => (
          <p key={f.name} className="small faint">
            {valueText(value[f.name])}
          </p>
        ))}
      </>
    );
  }
  return (
    <>
      {e.label !== undefined && recordKey === undefined && <h2>{e.label}</h2>}
      {fields.length === 0 && <Empty>Nothing to show.</Empty>}
      <dl className="kv">
        {fields.map((f) => (
          <span key={f.name} style={{ display: "contents" }}>
            <dt>{f.label}</dt>
            <dd>
              <Cell c={f} v={value[f.name]} to={fieldPath(f.name, value, s.tenant, may)} />
            </dd>
          </span>
        ))}
      </dl>
      {(e.actions ?? []).length > 0 && (
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          {(e.actions ?? [])
            .filter((a) => ctx.admin || !isAdmin(ctx, a))
            .map((a) => (
              <Action key={a.verb} ctx={ctx} e={a} rowKey={recordKey} />
            ))}
        </div>
      )}
    </>
  );
}

function isAdmin(ctx: Ctx, a: Element): boolean {
  return (
    ctx.m.workloads
      ?.find((w) => w.role === a.role)
      ?.serves?.some((v) => v.verb === a.verb && v.admin === true) === true
  );
}

/**
 * FormOpener is a declared form where the panel draws it: its name as a
 * button, which shows the form in the panel's place (§ conventions: a form).
 */
function FormOpener({ e }: { e: Element }) {
  const forms = useFormState();
  return (
    <div className="row">
      <Button
        onClick={() => {
          forms.show(formKey(e));
        }}
      >
        {formTitle(e)}
      </Button>
    </div>
  );
}

/** formTitle is a declared form's name: its label, or its verb where it declares none. */
function formTitle(e: Element): string {
  return e.label ?? e.verb ?? "Send";
}

/**
 * DeclaredForm is a solution's declared form in the panel's place, or in the
 * tab's with heading "h2": its fields, the refusals in the service's words,
 * *Cancel* and the saving action; on success, the sentence the declaration
 * gives and *Done*, which returns to the panel.
 */
export function DeclaredForm({
  ctx,
  e,
  back,
  heading,
  onClose,
}: {
  ctx: Ctx;
  e: Element;
  /** What the form stands in front of: the panel's title. */
  back: ReactNode;
  heading?: "h1" | "h2";
  onClose: () => void;
}) {
  const s = useSurface();
  const fields = e.fields ?? [];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [done, setDone] = useState(false);
  const submit = (ev: { preventDefault: () => void }) => {
    ev.preventDefault();
    const t = target(ctx, e);
    if (t === undefined) {
      setErr(unplaced(e.role));
      return;
    }
    const body: Record<string, unknown> = {};
    for (const f of fields) {
      const v = parseValue(f.kind, values[f.name] ?? "");
      if (v !== undefined) {
        body[f.name] = v;
      }
    }
    setBusy(true);
    setErr(undefined);
    setDone(false);
    s.bus.call(t.service, verbSubject(t.id, t.verb), body, 30000).then(
      () => {
        setBusy(false);
        setDone(true);
      },
      (x: unknown) => {
        setBusy(false);
        setErr(x);
      },
    );
  };
  const title = formTitle(e);
  return (
    <FormPage
      title={title}
      back={back}
      heading={heading}
      onClose={onClose}
      foot={
        done ? (
          <Button kind="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button kind="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button
              kind="primary"
              onClick={() => {
                submit({ preventDefault: () => undefined });
              }}
              disabled={busy}
            >
              {busy ? "Sending…" : title}
            </Button>
          </>
        )
      }
    >
      {err !== undefined && <Refused err={err} />}
      {done ? (
        <Notice kind="ok">{e.success ?? "Done."}</Notice>
      ) : (
        <form className="stack" onSubmit={submit}>
          {fields.map((f) => (
            <FieldInput
              key={f.name}
              f={f}
              value={values[f.name] ?? ""}
              disabled={busy}
              onChange={(v) => {
                setValues({ ...values, [f.name]: v });
              }}
            />
          ))}
        </form>
      )}
    </FormPage>
  );
}

/**
 * RecordPage is a list's row detail as the record's page, `<entry>/<panel>/<key>`
 * (14-solutions/03 § the elements; hq decision 0069): titled by the key in
 * monospace, every declared field the answer holds in the declared order, the
 * record's actions at its foot, and a crumb back to the panel. It reads its
 * record when it opens; Back returns to the list where the person left it.
 */
export function RecordPage({
  ctx,
  list,
  recordKey,
  crumb,
}: {
  ctx: Ctx;
  /** The panel's list whose record this is. */
  list: Element;
  recordKey: string;
  crumb: ReactNode;
}) {
  const record = list.record;
  if (!validRecordKey(recordKey)) {
    return (
      <>
        <PageHead crumb={crumb} title="Not a record" />
        <Empty>Nothing here is a record of that name.</Empty>
      </>
    );
  }
  return (
    <>
      <PageHead
        crumb={crumb}
        title={
          <span className="mono record-key" title={recordKey}>
            {recordKey}
          </span>
        }
        line={record?.label ?? list.label}
      />
      {record === undefined ? (
        <Empty>Nothing to show.</Empty>
      ) : (
        <section className="section">
          <RecordEl key={recordKey} ctx={ctx} e={record} recordKey={recordKey} />
        </section>
      )}
    </>
  );
}

function Action({ ctx, e, rowKey }: { ctx: Ctx; e: Element; rowKey?: string }) {
  const s = useSurface();
  const [err, setErr] = useState<unknown>(undefined);
  const [done, setDone] = useState(false);
  if (e.row_key !== undefined && rowKey === undefined) {
    return null; // a row's action, drawn on its row
  }
  const act = () => {
    const t = target(ctx, e);
    if (t === undefined) {
      setErr(unplaced(e.role));
      return;
    }
    setErr(undefined);
    setDone(false);
    s.bus.call(t.service, verbSubject(t.id, t.verb, rowKey), undefined, 30000).then(
      () => {
        setDone(true);
      },
      (x: unknown) => {
        setErr(x);
      },
    );
  };
  const label = e.label ?? e.verb ?? "";
  return (
    <span className="row">
      {e.undoable === true ? (
        <Button size="sm" onClick={act}>
          {label}
        </Button>
      ) : (
        <Confirm label={label} consequence={e.consequence ?? ""} onConfirm={act} />
      )}
      {done && <span className="small faint">done</span>}
      {err !== undefined && <Refused err={err} what={label} />}
    </span>
  );
}

function Chart({ ctx, e }: { ctx: Ctx; e: Element }) {
  const windows = e.windows ?? [];
  const [win, setWin] = useState<string>(windows[0] ?? "");
  const { value, err, done } = useStream<Point[]>(
    ctx,
    e,
    bodyOf(e, ctx.selection ?? NONE, win === "" ? undefined : { window: win }),
    (_prev, f) => pointsOf(f),
    [],
  );
  const select = e.selects !== undefined && ctx.select !== undefined ? ctx.select : undefined;
  const field = e.selects ?? "";
  const chosen = ctx.selection?.[field]?.value;
  return (
    <>
      <div className="section-head">
        <h2>{e.label ?? ""}</h2>
        {windows.length > 1 && (
          <Select
            value={win}
            onChange={setWin}
            options={windows.map((w) => ({
              value: w,
              label: w === "month" ? "this month" : `the last ${w}`,
            }))}
          />
        )}
      </div>
      {err !== undefined && <Refused err={err} />}
      {err === undefined &&
        value.length === 0 &&
        (done ? <Empty>Nothing yet.</Empty> : <Pending what="Reading" />)}
      {value.length > 0 && (
        <Series
          points={value}
          kind={e.chart ?? "line"}
          unit={e.unit}
          chosen={chosen === undefined ? undefined : dayOf(chosen)}
          onChoose={
            select === undefined
              ? undefined
              : (p) => {
                  select(field, p.at, dayOf(p.at));
                }
          }
        />
      )}
    </>
  );
}

function Series({
  points,
  kind,
  unit,
  chosen,
  onChoose,
}: {
  points: Point[];
  kind: "line" | "bar";
  unit?: string;
  /** The day selected, marked. */
  chosen?: string;
  /** onChoose selects a point, where the chart's points select. */
  onChoose?: (p: Point) => void;
}) {
  const W = 640;
  const H = 160;
  const max = Math.max(...points.map((p) => p.value), 1);
  const x = (i: number) => (points.length === 1 ? W / 2 : (i * W) / (points.length - 1));
  const y = (v: number) => H - (v / max) * (H - 8);
  const last = points.at(-1);
  return (
    <div className="card">
      <svg
        className="chart"
        viewBox={`0 0 ${String(W)} ${String(H + 2)}`}
        role="img"
        aria-label={`${String(points.length)} points`}
      >
        <line className="axis" x1={0} y1={H} x2={W} y2={H} />
        {kind === "bar" ? (
          points.map((p, i) => {
            const w = Math.max(W / points.length - 2, 1);
            const on = chosen !== undefined && dayOf(p.at) === chosen;
            if (onChoose === undefined) {
              return (
                <rect
                  key={p.at}
                  className="bar"
                  x={(i * W) / points.length}
                  y={y(p.value)}
                  width={w}
                  height={H - y(p.value)}
                />
              );
            }
            // The whole column opens, so an empty day can be chosen too.
            return (
              <g
                key={p.at}
                className={on ? "bar-pick chosen" : "bar-pick"}
                role="button"
                tabIndex={0}
                aria-pressed={on}
                aria-label={`${dayOf(p.at)}: ${String(p.value)} ${unit ?? ""}`}
                onClick={() => {
                  onChoose(p);
                }}
                onKeyDown={(ev) => {
                  if (opensOn(ev.key)) {
                    ev.preventDefault();
                    onChoose(p);
                  }
                }}
              >
                <rect className="bar-hit" x={(i * W) / points.length} y={0} width={w} height={H} />
                <rect
                  className="bar"
                  x={(i * W) / points.length}
                  y={y(p.value)}
                  width={w}
                  height={H - y(p.value)}
                />
              </g>
            );
          })
        ) : (
          <polyline
            className="line"
            points={points.map((p, i) => `${String(x(i))},${String(y(p.value))}`).join(" ")}
          />
        )}
      </svg>
      {last !== undefined && <p className="small faint">{peakLine(points, unit, when(last.at))}</p>}
    </div>
  );
}

function Status({ ctx, e }: { ctx: Ctx; e: Element }) {
  const { value, err } = useStream<string | undefined>(
    ctx,
    e,
    undefined,
    (prev, f) => stateOf(f) ?? prev,
    undefined,
  );
  const st = (e.states ?? []).find((x) => x.value === value);
  const tone = st?.kind === "live" ? "live" : st?.kind === "warn" ? "bad" : "neutral";
  const word = st?.word ?? value ?? "…";
  // The status says its word once: a label the word already begins with is not drawn beside it.
  const label = e.label !== undefined && !sameWord(word, e.label) ? e.label : undefined;
  return (
    <div className="row">
      {label !== undefined && <span className="small muted">{label}</span>}
      {err !== undefined ? (
        <Refused err={err} />
      ) : (
        <>
          <Dot tone={tone} />
          <Pill tone={tone}>{word}</Pill>
        </>
      )}
    </div>
  );
}

/** sameWord says whether a status's word already says its label: *recording* under *Recording*. */
export function sameWord(word: string, label: string): boolean {
  return word.toLocaleLowerCase().startsWith(label.toLocaleLowerCase());
}

/** SettingEl is one of the solution's settings on its panel: a tenant setting's field, a secret's *Store*, or where a person sets theirs. */
function SettingEl({ ctx, e }: { ctx: Ctx; e: Element }) {
  const s = useSurface();
  const setting = ctx.m.settings?.find((x) => x.name === e.setting);
  const secret = ctx.m.secrets?.find((x) => x.name === e.setting);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [saved, setSaved] = useState(false);
  const [held, setHeld] = useState<{ doc: Record<string, unknown>; revision?: number } | undefined>(
    undefined,
  );
  const plain = setting !== undefined && setting.scope === "tenant" && setting.secret !== true;
  useEffect(() => {
    if (!plain) {
      return;
    }
    s.bus
      .solutionConfig()
      .get(ctx.m.name, DOCUMENT_SETTINGS)
      .then(
        (entry) => {
          let doc: Record<string, unknown>;
          try {
            doc = JSON.parse(entry.value ?? "{}") as Record<string, unknown>;
          } catch {
            doc = {};
          }
          setHeld({ doc, revision: entry.revision });
        },
        (x: unknown) => {
          if (isRefusal(x, "not-found")) {
            setHeld({ doc: {} });
          } else {
            setErr(x);
          }
        },
      );
  }, [plain, s.bus, ctx.m.name]);
  if (setting?.scope === "person") {
    return (
      <p className="small">
        {setting.label} is yours to set, under{" "}
        <Link className="text-link" to={tenantPath(s.tenant, "me/credentials")}>
          Your credentials
        </Link>
        .
      </p>
    );
  }
  if (setting === undefined && secret === undefined) {
    return <Notice>This panel needs a newer console.</Notice>;
  }
  if (!plain) {
    const name = setting?.name ?? secret?.name ?? "";
    return (
      <div className="card stack">
        {err !== undefined && <NotAllowed err={err} what="Store" />}
        {saved && <Notice kind="ok">Stored. It is never shown again.</Notice>}
        <div className="row">
          <span className="grow">{setting?.label ?? name}</span>
          <TextInput
            type="password"
            value={value ?? ""}
            onChange={(v) => {
              setValue(v);
            }}
          />
          <Button
            size="sm"
            disabled={(value ?? "") === "" || busy}
            onClick={() => {
              setBusy(true);
              setErr(undefined);
              s.bus
                .vault()
                .store(secretPath(ctx.m.name, name), value ?? "")
                .then(
                  () => {
                    setBusy(false);
                    setValue("");
                    setSaved(true);
                  },
                  (x: unknown) => {
                    setBusy(false);
                    setErr(x);
                  },
                );
            }}
          >
            Store
          </Button>
        </div>
      </div>
    );
  }
  if (held === undefined) {
    return err !== undefined ? <NotAllowed err={err} /> : <Pending what="Reading the setting" />;
  }
  const shown = value ?? valueText(held.doc[setting.name] ?? setting.default);
  return (
    <form
      className="card stack"
      onSubmit={(ev) => {
        ev.preventDefault();
        const doc = { ...held.doc };
        const v = parseValue(setting.kind, shown);
        if (v === undefined) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- a cleared field leaves the document
          delete doc[setting.name];
        } else {
          doc[setting.name] = v;
        }
        setBusy(true);
        setErr(undefined);
        s.bus
          .solutionConfig()
          .set(ctx.m.name, DOCUMENT_SETTINGS, JSON.stringify(doc, null, 2) + "\n", held.revision)
          .then(
            (entry) => {
              setBusy(false);
              setSaved(true);
              setValue(undefined);
              setHeld({ doc, revision: entry.revision });
            },
            (x: unknown) => {
              setBusy(false);
              setErr(x);
            },
          );
      }}
    >
      {err !== undefined && <NotAllowed err={err} />}
      {saved && <Notice kind="ok">Saved: its workloads have it now.</Notice>}
      <FieldInput
        f={setting}
        value={shown}
        disabled={busy}
        onChange={(v) => {
          setSaved(false);
          setValue(v);
        }}
      />
      <div className="form-actions">
        <Button kind="primary" type="submit" disabled={busy || value === undefined}>
          Save
        </Button>
      </div>
    </form>
  );
}
